import React from 'react'
import { Alert, Button, Icon, Input, Upload } from 'antd'

import { starterPrompts } from '../../constants'
import {
  formatFileSize,
  getAcceptedAttribute,
  getReferenceKind,
  isStoppableBusyAction
} from '../../utils'
import './ConversationCard.scss'

const { TextArea } = Input

const ConversationCard = ({
  sessionId,
  messages,
  timelinePanel,
  scrollRef,
  errorMessage,
  isCreatingSession,
  isStreaming,
  isSynthesizingVision,
  isUploadingReference,
  isLockingVision,
  phaseBusyAction,
  acceptedExtensions,
  pendingReferences,
  activeWorkspace,
  draftMessage,
  onDraftMessageChange,
  onAttachReference,
  onRemovePendingReference,
  onSubmit,
  onStopAgentTurn,
  onKeyDown,
  onStarterPrompt
}) => {
  const canStopCurrentRequest =
    Boolean(onStopAgentTurn) &&
    (isStreaming ||
      isUploadingReference ||
      isStoppableBusyAction(phaseBusyAction))
  const isComposerBusy =
    !sessionId ||
    isStreaming ||
    isSynthesizingVision ||
    isCreatingSession ||
    isUploadingReference ||
    isLockingVision ||
    Boolean(phaseBusyAction)
  const hasPendingReferences = Boolean(pendingReferences.length)

  return (
    <section className="creative-direction-chat__studio-card creative-direction-chat__conversation">
      <div
        className={`creative-direction-chat__messages${
          !messages.length ? ' creative-direction-chat__messages--empty' : ''
        }`}
        ref={scrollRef}
      >
        {!messages.length ? (
          <div className="creative-direction-chat__starter-panel">
            <h4>How do you want to start?</h4>
            <div className="creative-direction-chat__starter-grid">
              {starterPrompts.map(item => (
                <button
                  type="button"
                  className={`creative-direction-chat__starter-card${
                    item.recommended
                      ? ' creative-direction-chat__starter-card--recommended'
                      : ''
                  }`}
                  key={item.key}
                  aria-label={`${item.actionLabel}: ${item.title}`}
                  disabled={
                    !sessionId ||
                    isStreaming ||
                    isSynthesizingVision ||
                    isCreatingSession ||
                    isLockingVision ||
                    Boolean(phaseBusyAction)
                  }
                  onClick={() => onStarterPrompt(item)}
                >
                  <div className="creative-direction-chat__starter-head">
                    <Icon type={item.icon} />
                  </div>
                  <strong>{item.title}</strong>
                  <small>{item.description}</small>
                  <div className="creative-direction-chat__starter-actions">
                    <span>
                      {item.actionLabel} <Icon type="arrow-right" />
                    </span>
                    {item.recommended ? <em>Recommended</em> : null}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {timelinePanel ? (
          <div className="creative-direction-chat__inline-workflow">
            {timelinePanel}
          </div>
        ) : null}
      </div>

      {errorMessage ? (
        <Alert
          className="creative-direction-chat__alert creative-direction-chat__alert--inline"
          type="error"
          showIcon
          message={errorMessage}
        />
      ) : null}

      <div
        className={`creative-direction-chat__interaction-dock${
          activeWorkspace
            ? ' creative-direction-chat__interaction-dock--with-phase'
            : ''
        }`}
      >
        {activeWorkspace ? (
          <div className="creative-direction-chat__dock-phase">
            {activeWorkspace}
          </div>
        ) : null}

        <div className="creative-direction-chat__composer">
          {hasPendingReferences ? (
            <div className="creative-direction-chat__pending-files">
              {pendingReferences.map(item => (
                <div
                  className="creative-direction-chat__pending-file"
                  key={item.id}
                >
                  <span className="creative-direction-chat__pending-preview">
                    {item.previewUrl ? (
                      <img src={item.previewUrl} alt="" />
                    ) : (
                      <Icon
                        type={
                          getReferenceKind(item.file) === 'video'
                            ? 'video-camera'
                            : 'picture'
                        }
                      />
                    )}
                  </span>
                  <span className="creative-direction-chat__pending-name">
                    {item.file.name}
                    {formatFileSize(item.file.size) ? (
                      <small>{formatFileSize(item.file.size)}</small>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${item.file.name}`}
                    disabled={isComposerBusy}
                    onClick={() => onRemovePendingReference(item.id)}
                  >
                    <Icon type="close" />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          <div className="creative-direction-chat__composer-box">
            <div className="creative-direction-chat__upload-control">
              <Upload
                accept={getAcceptedAttribute(acceptedExtensions)}
                multiple
                showUploadList={false}
                beforeUpload={onAttachReference}
                disabled={isComposerBusy}
              >
                <button
                  type="button"
                  className="creative-direction-chat__attach-icon"
                  disabled={isComposerBusy}
                  aria-label="Attach image or video reference"
                  title="Attach image or video reference"
                >
                  <Icon type="paper-clip" />
                </button>
              </Upload>
            </div>
            <TextArea
              className="creative-direction-chat__composer-input"
              value={draftMessage}
              rows={1}
              disabled={isComposerBusy}
              aria-label="Message"
              placeholder="Type your answer or ask for a refinement..."
              onChange={event => onDraftMessageChange(event.target.value)}
              onKeyDown={onKeyDown}
            />
            {canStopCurrentRequest ? (
              <Button
                className="creative-direction-chat__stop-button"
                icon="close-circle"
                onClick={onStopAgentTurn}
              />
            ) : (
              <Button
                type="primary"
                className="creative-direction-chat__send-button"
                loading={isUploadingReference}
                disabled={!draftMessage.trim() || isComposerBusy}
                onClick={onSubmit}
              >
                Send
                <Icon type="arrow-right" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default ConversationCard
